import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Grid, Paper, Typography, Button, Box, Avatar, Divider, useMediaQuery, Chip } from '@mui/material';
import { WhatsApp, Email, Phone, LocationOn, Star } from '@mui/icons-material';
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase-config";
import './DealerPage.css';

const DealerPage = () => {
  const { dealerSlug } = useParams();
  const [dealer, setDealer] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    const fetchDealerData = async () => {
      try {
        const q = query(collection(db, "users"), where("dealerSlug", "==", dealerSlug));
        const snapshot = await getDocs(q);
  
        if (snapshot.empty) {
          setLoading(false);
          return;
        }
  
        const dealerDoc = snapshot.docs[0];
        const dealerData = dealerDoc.data();
        const dealerId = dealerDoc.id;
  
        setDealer(dealerData);
  
        const adsQuery = query(
          collection(db, "ads"),
          where("userId", "==", dealerId),
          where("status", "==", "active")
        );
        const adsSnapshot = await getDocs(adsQuery);
  
        const dealerAds = adsSnapshot.docs.map(adDoc => ({
          id: adDoc.id,
          ...adDoc.data()
        }));
  
        setCars(dealerAds);
      } catch (error) {
        console.error("Грешка:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchDealerData();
  }, [dealerSlug]);

  const getWhatsAppLink = () => {
    const phone = dealer?.phone?.replace(/\s+/g, '');
    return `https://wa.me/${phone}?text=Здраво,%20дали%20е%20достапен%20вашиот%20оглас%20за%20автомобил?`;
  };

  if (loading) return <div className="loading-spinner">Се вчитува...</div>;
  if (!dealer) return <div className="not-found">Дилерот не е пронајден!</div>;

  return (
    <div className="page-container">
      {/* Google Ads Sidebar Left - Desktop Only */}
      {!isTablet && (
        <Box className="ad-sidebar left">
          <Paper elevation={1} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Реклама
            </Typography>
            <Box sx={{ 
              width: '160px', 
              height: '600px', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              Google Ad (160x600)
            </Box>
          </Paper>
        </Box>
      )}

      {/* Main Content */}
      <Box className="dealer-content" sx={{ px: isMobile ? 1 : 2, py: 4 }}>
        {/* Dealer Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 4,
          gap: 3,
          backgroundColor: 'white',
          p: isMobile ? 2 : 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          {dealer.avatar ? (
            <Avatar 
              src={dealer.avatar} 
              alt="Лого на салонот" 
              variant="rounded"
              sx={{ 
                width: isMobile ? 80 : 120, 
                height: isMobile ? 80 : 120, 
                bgcolor: '#f5f5f5',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          ) : (
            <Avatar 
              variant="rounded"
              sx={{ 
                width: isMobile ? 80 : 120, 
                height: isMobile ? 80 : 120, 
                bgcolor: '#e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {dealer.dealerName?.charAt(0) || 'A'}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', mb: 1 }}>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold', mr: 2 }}>
                {dealer.dealerName}
              </Typography>
              <Chip 
                icon={<Star fontSize="small" />} 
                label="Официјален дилер" 
                color="primary" 
                size="small" 
                variant="outlined"
              />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
              <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              {dealer.city}, {dealer.address}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <Button 
                variant="contained" 
                size="small"
                startIcon={<Phone />}
                href={`tel:${dealer.phone}`}
                sx={{ 
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' }
                }}
              >
                Јавете се
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<WhatsApp />}
                href={getWhatsAppLink()}
                target="_blank"
                sx={{ 
                  backgroundColor: '#25D366',
                  '&:hover': { backgroundColor: '#1da851' }
                }}
              >
                WhatsApp
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Email />}
                href={`mailto:${dealer.email}`}
              >
                Е-пошта
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Mobile Top Ad Banner */}
        {isMobile && (
          <Box sx={{ mb: 3 }}>
            <Paper elevation={1} sx={{ p: 1, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Реклама
              </Typography>
              <Box sx={{ 
                width: '100%', 
                height: '100px', 
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1
              }}>
                Google Ad (320x100)
              </Box>
            </Paper>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Автомобили / Огласи */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                Автомобили во понуда ({cars.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cars.length} резултати
              </Typography>
            </Box>
            
            {cars.length === 0 ? (
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Моментално нема достапни автомобили од овој дилер.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={isMobile ? 2 : 3}>
                {cars.map(car => (
                  <Grid item xs={12} sm={6} md={4} key={car.id}>
                    <Paper elevation={0} sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: 'transparent'
                      }
                    }}>
                      <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        height: isMobile ? '140px' : '160px',
                        mb: 2,
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        <img
                          src={
                            car.images && car.images.length > 0
                              ? car.images[car.coverPhotoIndex || 0]
                              : 'https://via.placeholder.com/400x180?text=Нема+слика'
                          }
                          alt={car.model}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 1,
                          borderRadius: 1,
                          fontSize: '0.8rem'
                        }}>
                          {car.year}
                        </Box>
                      </Box>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 'bold', 
                        flexGrow: 1,
                        mb: 1,
                        fontSize: isMobile ? '1rem' : '1.1rem'
                      }}>
                        {car.make} {car.model}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {car.mileage} km • {car.fuelType}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mt: 'auto'
                      }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {car.price} €
                        </Typography>
                        <Button
                          component={Link}
                          to={`/cars/${car.id}`}
                          variant="contained"
                          size="small"
                          sx={{ 
                            backgroundColor: '#FF5722',
                            '&:hover': { backgroundColor: '#E64A19' },
                            fontSize: '0.75rem'
                          }}
                        >
                          Види детали
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Контакт */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} sx={{ 
              p: isMobile ? 2 : 3, 
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              position: isMobile ? 'static' : 'sticky',
              top: 20
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                textAlign: 'center',
                fontWeight: 'bold',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}>
                <LocationOn color="primary" /> Информации за салон
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>Адреса:</Typography>
                <Typography variant="body1">
                  <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {dealer.city}, {dealer.address}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>Телефон:</Typography>
                <Typography variant="body1">
                  <Phone fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {dealer.phone}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>Е-пошта:</Typography>
                <Typography variant="body1">
                  <Email fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {dealer.email}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Контактирајте не директно
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<Phone />}
                  fullWidth
                  href={`tel:${dealer.phone}`}
                  sx={{ py: 1.5 }}
                >
                  Јавете се сега
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WhatsApp />}
                  href={getWhatsAppLink()}
                  target="_blank"
                  fullWidth
                  sx={{ py: 1.5, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1da851' } }}
                >
                  Пратете порака
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Email />}
                  href={`mailto:${dealer.email}`}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  Испрати е-пошта
                </Button>
              </Box>

              {dealer.location && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Локација на салон
                  </Typography>
                  <iframe
                    src={dealer.location}
                    width="100%"
                    height={isMobile ? '200' : '250'}
                    style={{ border: 0, borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Dealer Location"
                  ></iframe>
                </Box>
              )}
            </Paper>

            {/* Mobile Bottom Ad Banner */}
            {isMobile && (
              <Box sx={{ mt: 3 }}>
                <Paper elevation={1} sx={{ p: 1, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Реклама
                  </Typography>
                  <Box sx={{ 
                    width: '100%', 
                    height: '100px', 
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1
                  }}>
                    Google Ad (320x100)
                  </Box>
                </Paper>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Google Ads Sidebar Right - Desktop Only */}
      {!isTablet && (
        <Box className="ad-sidebar right">
          <Paper elevation={1} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Реклама
            </Typography>
            <Box sx={{ 
              width: '160px', 
              height: '600px', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              Google Ad (160x600)
            </Box>
          </Paper>
        </Box>
      )}
    </div>
  );
};

export default DealerPage;